import { Injectable, NgZone } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslateService } from '@ngx-translate/core';
import {
  BehaviorSubject,
  combineLatest,
  distinctUntilChanged,
  map,
  Subject,
  switchMap,
} from 'rxjs';
import { TrackType } from 'src/app/common/interfaces/rtc-interface';
import { DeviceType } from 'src/app/common/utils/devices-handle-util';
import { DeviceErrorType, DeviceState, DeviceTogglingStatus } from 'src/app/models/device-manger';
import { DevicesManagerService } from 'src/app/services/devices-manager.service';
import { LocalTracksManagerService } from 'src/app/services/local-tracks-manager.service';
import { ProviderStateService } from 'src/app/services/provider-state.service';
import { SpacePermissionsManagerService } from 'src/app/services/space-permissions-manager.service';
import { VideoCallTracksStateService } from 'src/app/services/video-call-tracks-state.service';
import { TruncateStringPipe } from 'src/app/standalones/pipes/truncate-string.pipe';
import { enterZone } from 'src/app/utilities/ZoneUtils';
import { KeyboardShortcutsCheckService } from 'src/app/services/keyboard-shortcuts-check.service';
import { PictureInPictureService } from './picture-in-picture.service';
import { VideoCallControlsService } from './video-call-controls.service';

@UntilDestroy()
@Injectable()
export class CamButtonStateService {
  private readonly _disabled$ = new BehaviorSubject<boolean>(false);
  readonly disabled$ = this._disabled$.asObservable();

  /** Pre-join unmute failed; join-call panel subscribes to open the helper guide. */
  private readonly _joinPreviewCamUnmuteFailed$ = new Subject<void>();
  readonly joinPreviewCamUnmuteFailed$ = this._joinPreviewCamUnmuteFailed$.asObservable();

  constructor(
    private localTracksManager: LocalTracksManagerService,
    private providerStateService: ProviderStateService,
    private videoCallTracksStateService: VideoCallTracksStateService,
    private translateService: TranslateService,
    private spacePermissionsManagerService: SpacePermissionsManagerService,
    private devicesManagerService: DevicesManagerService,
    private ngZone: NgZone,
    private pictureInPictureService: PictureInPictureService,
    private videoCallControlsService: VideoCallControlsService,
    private truncateStringPipe: TruncateStringPipe,
    private keyboardShortcutsCheckService: KeyboardShortcutsCheckService,
  ) {
    const disabledSource$ = combineLatest([
      this.providerStateService.callConnected$,
      this.spacePermissionsManagerService.canOpenCam$.pipe(enterZone(this.ngZone)),
      this.camLoading$,
    ]).pipe(
      map(([isCallConnected, canOpenCam, camLoading]) =>
        !isCallConnected ? camLoading : !canOpenCam || camLoading,
      ),
      distinctUntilChanged(),
    );

    disabledSource$.pipe(untilDestroyed(this)).subscribe((disabled) => {
      this._disabled$.next(disabled);
    });
  }

  isCamOpen$ = this.providerStateService.callConnected$.pipe(
    switchMap((isCallConnected) => {
      if (!isCallConnected) {
        return this.localTracksManager.camState$.pipe(
          map((s) => s.state),
          distinctUntilChanged(),
        );
      }
      return this.providerStateService.localParticipantId$.pipe(
        switchMap((localParticipantId) =>
          this.videoCallTracksStateService.getPariticpantTrackPlayingObservable(
            localParticipantId,
            TrackType.VIDEO,
            this,
          ),
        ),
      );
    }),
  );

  camLabel$ = this.isCamOpen$.pipe(
    map((camOpen) =>
      camOpen
        ? this.translateService.instant('Stop Video')
        : this.translateService.instant('Start Video'),
    ),
  );

  camLoading$ = this.providerStateService.callConnected$.pipe(
    switchMap((isCallConnected) => {
      if (!isCallConnected) {
        return this.localTracksManager.camTogglingStatus$.pipe(
          map((status) => status === DeviceTogglingStatus.LOADING),
          distinctUntilChanged(),
        );
      }
      return combineLatest([
        this.localTracksManager.camTogglingStatus$,
        this.providerStateService.callReconnecting$,
      ]).pipe(
        map(
          ([deviceTogglingStatus, callReconnecting]) =>
            deviceTogglingStatus === DeviceTogglingStatus.LOADING || callReconnecting,
        ),
        distinctUntilChanged(),
      );
    }),
  );

  hasError$ = combineLatest([this.localTracksManager.camState$, this.camLoading$]).pipe(
    map(([state, camLoading]) => state.hasError && !camLoading),
    distinctUntilChanged(),
  );

  camIcon$ = combineLatest([
    this.disabled$,
    this.isCamOpen$,
    this.hasError$,
    this.videoCallControlsService.showNewVctRightControls$,
    this.providerStateService.callConnected$,
  ]).pipe(
    map(([disabled, camOpen, hasError, showNewVctRightControls, isCallConnected]) => {
      if (hasError) {
        return isCallConnected ? 'videocam_off_with_error_white' : 'videocam_off';
      } else if (camOpen || (disabled && !showNewVctRightControls)) {
        return 'videocam_icon';
      } else {
        return 'videocam_off';
      }
    }),
  );

  camTooltip$ = combineLatest([
    this.localTracksManager.camState$,
    this.camLoading$,
    this.isCamOpen$,
    this.spacePermissionsManagerService.canOpenCam$.pipe(enterZone(this.ngZone)),
    this.providerStateService.callConnected$,
  ]).pipe(
    map(([camState, camLoading, isCamEnabled, canOpenCam, isCallConnected]) => {
      const isJoinCallPanelActive = !isCallConnected;
      if (!isJoinCallPanelActive && !canOpenCam) {
        return {
          text: this.translateService.instant('The host has disabled this control'),
        };
      }
      if (camLoading) {
        if (this.providerStateService.callReconnectingValue) {
          return {
            text: this.translateService.instant(
              'Your network is unstable.\nReconnecting you to the call.',
            ),
          };
        } else {
          return {
            text: this.translateService.instant('Setting-up your camera. Please wait a moment.'),
          };
        }
      }
      if (camState.hasError) {
        return {
          text: this.devicesManagerService.camErrorsMappings?.get(camState.errorType!),
          error: true,
        };
      }
      if (isCamEnabled) {
        return {
          text: this.translateService.instant('Turn off camera'),
        };
      } else {
        return {
          text: this.translateService.instant('Turn on camera'),
        };
      }
    }),
  );

  tooltipSigns$ = combineLatest([this.camTooltip$, this.disabled$]).pipe(
    map(([camTooltip, disabled]) => {
      if (!camTooltip.error && !disabled && camTooltip.text) {
        return [this.keyboardShortcutsCheckService.getAltKeySign(), 'V'];
      }
      return [];
    }),
  );

  tooltipDisabled$ = this.pictureInPictureService.isPIPActive$.pipe(
    map((isPIPActive) => isPIPActive),
  );

  // ========== Device Menu State ==========
  // State for the camera device selection menu

  deviceMenuCamHasError$ = this.localTracksManager.camState$.pipe(
    map((camState) => camState.hasError),
  );

  private _deviceCamLabel$ = this.localTracksManager.camState$.pipe(
    map((camState) => this.truncateStringPipe.transform(camState.label ?? 'Default camera', 25)),
  );

  deviceMenuCamLabel$ = combineLatest([
    this._deviceCamLabel$,
    this.localTracksManager.camState$,
  ]).pipe(map(([camName, camState]) => this.getDeviceMenuCamLabel(camState, camName)));

  deviceMenuCamIcon$ = this.localTracksManager.camState$.pipe(
    map((camState) => (camState.hasError ? 'videocam_off_with_error_white' : 'cam-loading')),
  );

  deviceMenuCamSubtitleError$ = this.localTracksManager.camState$.pipe(
    map((camState) => {
      const error = camState.errorType;
      if (
        error &&
        [
          DeviceErrorType.NO_INPUT_DETECTED,
          DeviceErrorType.DEFAULT_ERROR,
          DeviceErrorType.GET_USER_MEDIA_TIMEOUT,
        ].includes(error)
      ) {
        return this.translateService.instant('No input from device');
      }
      return undefined;
    }),
  );

  private getDeviceMenuCamLabel(camState: Readonly<DeviceState>, camName: string): string {
    if (!camState.hasError) {
      return camName;
    }
    switch (camState.errorType) {
      case DeviceErrorType.PERMISSION_DENIED:
      case DeviceErrorType.PERMISSION_DENIED_BY_SYSTEM:
        return this.translateService.instant('Permission required');
      case DeviceErrorType.NOT_FOUND:
        return this.translateService.instant('No camera available');
      default:
        return camName;
    }
  }

  openDeviceSelection(): void {
    this.devicesManagerService.openDeviceSelection();
  }

  async toggleCam(): Promise<void> {
    // Prevent toggling when disabled (includes loading state or permission denied)
    if (this._disabled$.value) {
      return;
    }
    if (!this.providerStateService.callConnectedValue) {
      const wasOff = !this.localTracksManager.getCamDeviceState().state;
      try {
        await this.localTracksManager.toggleJoinPreviewCam();
      } catch {
        // Device errors are surfaced via joinPreviewCamUnmuteFailed$ when unmute fails.
      }
      if (wasOff && this.localTracksManager.getCamDeviceState().hasError) {
        this._joinPreviewCamUnmuteFailed$.next();
      }
      return;
    }
    await this.localTracksManager.toggleDeviceState(DeviceType.VIDEO, true);
  }
}
